import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { cn } from '../lib/cn';

export const badgeVariants = cva(
  'inline-flex items-center gap-1.5 border px-2 py-0.5 text-xs leading-4 font-medium whitespace-nowrap [&_svg]:size-3 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'rounded-md border-primary/15 bg-primary/10 text-primary',
        secondary: 'rounded-md border-transparent bg-muted text-muted-foreground',
        outline: 'rounded-md border-border bg-card text-foreground',
        success: 'rounded-full border-success/20 bg-success/10 text-success',
        warning: 'rounded-full border-warning/25 bg-warning/12 text-warning',
        destructive: 'rounded-full border-destructive/20 bg-destructive/10 text-destructive',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

export interface BadgeProps extends ComponentProps<'span'>, VariantProps<typeof badgeVariants> {
  /**
   * Punto de estado a la izquierda. Por defecto lo llevan las variantes de estado (éxito, aviso,
   * error): así el estado no depende sólo del color del texto.
   */
  dot?: boolean;
}

const STATUS = new Set(['success', 'warning', 'destructive']);

export function Badge({ className, variant, dot, children, ...props }: BadgeProps) {
  const showDot = dot ?? STATUS.has(variant ?? '');
  return (
    <span className={cn(badgeVariants({ variant }), className)} {...props}>
      {showDot ? (
        <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-current opacity-90" />
      ) : null}
      {children}
    </span>
  );
}
